import type { ApiClient } from '../client';

/**
 * One entry of the pool-offer available-categories list. `id` is the
 * *representative pool_offer_id* for this sport — that's the value that lives
 * in the page URL as `?game_id=…`, so matching `c.id === currentGameId`
 * resolves the active header pill.
 */
export interface AvailableCategory {
  /** Representative pool_offer_id (what the URL's `game_id` matches). */
  id: number;
  /** Sport / collection label to render on the pill ("Baseball", "Golf"…). */
  label: string;
  /** Icon URL (Rails Active Storage / S3). */
  iconSrc: string;
  /**
   * Backing category record id — pass to `listPoolOffers` to fetch every
   * pack in this category (e.g. all Baseball offers).
   */
  categoryId: number;
  /**
   * Ruby class name for the category record. For the iframe this is always
   * `LootMachine::PoolCategory` — server is the sole source of truth for
   * the value, no client-side fallback.
   */
  categoryClassName: string;
}

interface RawCategory {
  id: number | string;
  category_id?: number | string;
  category_name?: string;
  category_type?: string;
  image?: string | null;
}

/**
 * `GET /loot_machine/api/game_platform/iframe/v1/pool_categories`
 *
 * Returns the header's pill row, scoped to pool_categories that the
 * platform_session's operator can actually serve. Authenticated via the
 * `X-Platform-Session` header that the iframe client already attaches.
 */
export async function listAvailableCategories(
  client: ApiClient,
): Promise<AvailableCategory[]> {
  const raw = await client.request<{ categories: RawCategory[] }>(
    '/loot_machine/api/game_platform/iframe/v1/pool_categories',
    { method: 'GET' },
  );
  return (raw.categories ?? []).map((c) => ({
    id: Number(c.id),
    label: c.category_name ?? '',
    iconSrc: c.image ?? '',
    categoryId: Number(c.category_id ?? 0),
    categoryClassName: c.category_type ?? 'LootMachine::PoolCategory',
  }));
}
